/**
 * LinkedIn Ads Adapter
 * 
 * Adapts event data into LinkedIn Insight Tag format.
 * LinkedIn uses conversion IDs configured in the LinkedIn Ads platform.
 * 
 * Documentation:
 * - https://www.linkedin.com/help/lms/answer/a7488563 (Dynamic conversion values)
 * - https://www.linkedin.com/help/lms/answer/a528686 (Conversion event types)
 * 
 * @since 1.51.0
 */

(function (pmw, undefined) {

	class LinkedInAdapter {
		
		/**
		 * Adapt event data to LinkedIn Insight Tag format
		 * 
		 * @param {object} eventData - Event-specific data (product, order, etc.)
		 * @param {string} eventName - Standardized event name
		 * @param {object} context - Event context (user, page, etc.)
		 * @returns {object|null} Adapted data for LinkedIn or null if pixel not loaded
		 */
		adapt(eventData, eventName, context) {
			
			// Check if LinkedIn pixel is loaded
			if (!pmwDataLayer?.pixels?.linkedin?.loaded) {
				return null;
			}
			
			const eventMap = {
				'view_item': () => this.adaptViewContent(eventData),
				'add_to_cart': () => this.adaptAddToCart(eventData),
				'purchase': () => this.adaptPurchase(eventData),
			};
			
			if (!eventMap[eventName]) {
				return null;
			}
			
			return eventMap[eventName]();
		}
		
		/**
		 * Get conversion ID for a specific event
		 */
		getConversionId(eventType) {
			return pmwDataLayer?.pixels?.linkedin?.conversion_ids?.[eventType] || null;
		}
		
		/**
		 * Adapt view_content event
		 * 
		 * LinkedIn conversion type: key_page_view
		 */
		adaptViewContent(eventData) {
			const conversion_id = this.getConversionId('view_content');
			if (!conversion_id) return null;
			
			const data = {
				conversion_id: conversion_id,
				conversion_type: 'key_page_view',
			};
			
			// Add conversion value if product data is available
			if (eventData?.product) {
				data.conversion_value = eventData.product.quantity * eventData.product.price;
				data.conversion_currency = pmwDataLayer?.shop?.currency;
			}
			
			return data;
		}
		
		/**
		 * Adapt add_to_cart event
		 * 
		 * LinkedIn conversion type: add_to_cart
		 */
		adaptAddToCart(eventData) {
			const conversion_id = this.getConversionId('add_to_cart');
			if (!conversion_id) return null;
			
			const data = {
				conversion_id: conversion_id,
				conversion_type: 'add_to_cart',
			};
			
			// Add conversion value if product data is available
			if (eventData?.product) {
				data.conversion_value = eventData.product.quantity * eventData.product.price;
				data.conversion_currency = pmwDataLayer?.shop?.currency;
			}
			
			return data;
		}
		
		/**
		 * Adapt purchase event
		 * 
		 * LinkedIn conversion type: purchase
		 */
		adaptPurchase(eventData) {
			const conversion_id = this.getConversionId('purchase');
			if (!conversion_id) return null;
			
			return {
				conversion_id: conversion_id,
				conversion_type: 'purchase',
				conversion_value: pmwDataLayer.order.value.marketing,
				conversion_currency: pmwDataLayer.order.currency,
			};
		}
		
	}
	
	// Register the adapter
	pmw.registerAdapter('linkedin', new LinkedInAdapter(), {
		supportsS2S: false,
		supportsEventDeduplication: false,
		isActive: () => pmwDataLayer?.pixels?.linkedin?.partner_id ? true : false
	});
	
}(window.pmw = window.pmw || {}));
